import { useCustom, useCustomMutation, useInvalidate } from "@refinedev/core";
import { useParams } from "react-router-dom";
import {
  Button,
  Card,
  Descriptions,
  Divider,
  Empty,
  Form,
  Input,
  message,
  Modal,
  Popconfirm,
  Row,
  Col,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  LockOutlined,
  PlusOutlined,
  TrophyOutlined,
} from "@ant-design/icons";
import { useState } from "react";
import { apiUrl } from "@/consts";
import UserSearchSelect from "@/pages/components/UserSearchSelect";
import { PlayerCardModal } from "./components/PlayerCardModal";

const { Title, Text } = Typography;

type ScoutingEvent = {
  playerCardTemplateKey: string | null;
  id: string;
  title: string;
  location: string;
  startTime: string;
  description: string | null;
  status: "DRAFT" | "ACTIVE" | "COMPLETED" | "CANCELLED";
  leaderboardUrl: string;
  registrationUrl: string | null;
  clubId: string | null;
  teamId: string | null;
  club?: { id: string; name: string } | null;
  team?: { id: string; name: string } | null;
};

type EventEmployee = {
  id: string;
  eventId: string;
  allowedMetrics: string[];
  canLockScore: boolean;
  employee: {
    id: string;
    username: string;
    fullName?: string | null;
    email: string;
  };
};

type PlayerCardRow = {
  id: string;
  userId: string;
  rank: number;
  imageUrl: string;
  isPinned: boolean;
  revealed: boolean;
  playerName: string | null;
  playerEmail: string | null;
};

const STATUS_COLOR: Record<string, string> = {
  DRAFT: "default",
  ACTIVE: "success",
  COMPLETED: "blue",
  CANCELLED: "red",
};

const ALL_METRICS: { value: string; label: string }[] = [
  { value: "acc", label: "10m Sprint (acc)" },
  { value: "spe", label: "30m Sprint (spe)" },
  { value: "agi", label: "Pro Agility (agi)" },
  { value: "dri", label: "Slalom (dri)" },
  { value: "sho", label: "Accuracy (sho)" },
  { value: "pow", label: "CMJ (pow)" },
];

export function ScoutingEventShow() {
  const { id } = useParams<{ id: string }>();
  const invalidate = useInvalidate();
  const { mutate } = useCustomMutation();

  // Employee assignment state
  const [assignState, setAssignState] = useState<{
    userId: string;
    metrics: string[];
    canLockScore: boolean;
  }>({ userId: "", metrics: [], canLockScore: false });
  const [assigning, setAssigning] = useState(false);
  const [lockingScores, setLockingScores] = useState(false);
  const [removingEmployee, setRemovingEmployee] = useState<
    Record<string, boolean>
  >({});
  const [editAssignmentModal, setEditAssignmentModal] = useState<{
    open: boolean;
    employeeUserId: string;
    metrics: string[];
    canLockScore: boolean;
    saving: boolean;
  }>({
    open: false,
    employeeUserId: "",
    metrics: [],
    canLockScore: false,
    saving: false,
  });
  const [inviteModal, setInviteModal] = useState<{
    open: boolean;
    email: string;
    fullName: string;
  }>({ open: false, email: "", fullName: "" });
  const [invitingEmployee, setInvitingEmployee] = useState(false);

  const refresh = () => {
    invalidate({ resource: "scouting-event", invalidates: ["all"] });
    eventQuery.refetch();
    employeesQuery.refetch();
  };

  const eventQuery = useCustom<{ event: ScoutingEvent }>({
    url: `${apiUrl}/scouting-event/${id}`,
    method: "get",
    queryOptions: { enabled: !!id },
  });

  const employeesQuery = useCustom<{ employees: EventEmployee[] }>({
    url: `${apiUrl}/scouting-event/${id}/employees`,
    method: "get",
    queryOptions: { enabled: !!id },
  });

  const playerCardsQuery = useCustom<{ data: PlayerCardRow[] }>({
    url: `${apiUrl}/scouting-event/${id}/player-cards`,
    method: "get",
    queryOptions: { enabled: !!id },
  });

  const event = eventQuery.data?.data?.event;
  const employees = employeesQuery.data?.data?.employees ?? [];
  const playerCards = playerCardsQuery.data?.data?.data ?? [];

  const [cardModal, setCardModal] = useState<{
    open: boolean;
    imageUrl?: string;
    playerName?: string | null;
    playerEmail?: string | null;
  }>({ open: false });

  // ── Event update (status) ────────────────────────────────────────────────

  function handleStatusChange(status: string) {
    mutate(
      {
        url: `${apiUrl}/scouting-event/${id}`,
        method: "put",
        values: { status },
      },
      {
        onSuccess: () => {
          message.success("Status updated");
          refresh();
        },
        onError: (err: any) =>
          message.error(err?.message ?? "Failed to update status"),
      }
    );
  }

  // ── Lock scores ──────────────────────────────────────────────────────────

  function handleLock() {
    if (!event?.playerCardTemplateKey) {
      message.error("Upload a player card template before locking scores");
      return;
    }
    setLockingScores(true);
    mutate(
      {
        url: `${apiUrl}/scouting-event/${id}/lock`,
        method: "post",
        values: {},
      },
      {
        onSuccess: () => {
          message.success("Scores locked");
          setLockingScores(false);
          refresh();
        },
        onError: (err: any) => {
          message.error(err?.message ?? "Failed to lock scores");
          setLockingScores(false);
        },
      }
    );
  }

  // ── Assign employee ──────────────────────────────────────────────────────

  function handleAssignEmployee() {
    if (!assignState.userId) {
      message.error("Select a user first");
      return;
    }
    setAssigning(true);
    mutate(
      {
        url: `${apiUrl}/scouting-event/${id}/employees`,
        method: "post",
        values: {
          employeeUserId: assignState.userId,
          allowedMetrics:
            assignState.metrics.length > 0 ? assignState.metrics : undefined,
          canLockScore: assignState.canLockScore ?? false,
        },
      },
      {
        onSuccess: () => {
          message.success("Employee assigned");
          setAssignState({ userId: "", metrics: [], canLockScore: false });
          setAssigning(false);
          refresh();
        },
        onError: (err: any) => {
          message.error(err?.message ?? "Failed to assign employee");
          setAssigning(false);
        },
      }
    );
  }

  // ── Remove employee ──────────────────────────────────────────────────────

  function handleRemoveEmployee(employeeUserId: string) {
    setRemovingEmployee((prev) => ({ ...prev, [employeeUserId]: true }));
    mutate(
      {
        url: `${apiUrl}/scouting-event/${id}/employees/${employeeUserId}`,
        method: "delete",
        values: {},
      },
      {
        onSuccess: () => {
          message.success("Employee removed");
          setRemovingEmployee((prev) => ({ ...prev, [employeeUserId]: false }));
          refresh();
        },
        onError: (err: any) => {
          message.error(err?.message ?? "Failed to remove employee");
          setRemovingEmployee((prev) => ({ ...prev, [employeeUserId]: false }));
        },
      }
    );
  }

  function openEditAssignmentModal(emp: EventEmployee) {
    setEditAssignmentModal({
      open: true,
      employeeUserId: emp.employee.id,
      metrics: [...emp.allowedMetrics],
      canLockScore: emp.canLockScore,
      saving: false,
    });
  }

  function handleUpdateAssignment() {
    const { employeeUserId, metrics, canLockScore } = editAssignmentModal;
    setEditAssignmentModal((prev) => ({ ...prev, saving: true }));
    mutate(
      {
        url: `${apiUrl}/scouting-event/${id}/employees/${employeeUserId}`,
        method: "patch",
        values: { allowedMetrics: metrics, canLockScore },
      },
      {
        onSuccess: () => {
          message.success("Assignment updated");
          setEditAssignmentModal({
            open: false,
            employeeUserId: "",
            metrics: [],
            canLockScore: false,
            saving: false,
          });
          refresh();
        },
        onError: (err: any) => {
          message.error(err?.message ?? "Failed to update assignment");
          setEditAssignmentModal((prev) => ({ ...prev, saving: false }));
        },
      }
    );
  }

  function openInviteModal(seed: string) {
    setInviteModal({
      open: true,
      email: seed.includes("@") ? seed : "",
      fullName: "",
    });
  }

  function handleInviteAndAssign() {
    if (!inviteModal.email.trim() || !inviteModal.fullName.trim()) {
      message.error("Please provide employee name and email");
      return;
    }
    setInvitingEmployee(true);
    mutate(
      {
        url: `${apiUrl}/scouting-event/${id}/employees/invite`,
        method: "post",
        values: {
          email: inviteModal.email.trim(),
          name: inviteModal.fullName.trim(),
        },
      },
      {
        onSuccess: (inviteRes: any) => {
          const employeeUserId = inviteRes?.data?.userId;
          if (!employeeUserId) {
            message.error("Invitation created but user id missing");
            setInvitingEmployee(false);
            return;
          }
          mutate(
            {
              url: `${apiUrl}/scouting-event/${id}/employees`,
              method: "post",
              values: {
                employeeUserId,
                allowedMetrics: assignState.metrics.length
                  ? assignState.metrics
                  : undefined,
                canLockScore: assignState.canLockScore ?? false,
              },
            },
            {
              onSuccess: () => {
                message.success("Employee invited and assigned");
                setAssignState({
                  userId: "",
                  metrics: [],
                  canLockScore: false,
                });
                setInviteModal({ open: false, email: "", fullName: "" });
                setInvitingEmployee(false);
                refresh();
              },
              onError: (err: any) => {
                message.error(
                  err?.message ?? "Employee invited but assignment failed"
                );
                setInvitingEmployee(false);
              },
            }
          );
        },
        onError: (err: any) => {
          message.error(err?.message ?? "Failed to invite employee");
          setInvitingEmployee(false);
        },
      }
    );
  }

  if (eventQuery.isLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!event) {
    return <Text type="danger">Event not found.</Text>;
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 24,
        padding: "0 8px",
      }}
    >
      {/* ── Event Details ────────────────────────────────────────────────── */}
      <Card
        title={
          <Title level={4} style={{ margin: 0 }}>
            {event.title}
          </Title>
        }
      >
        <Descriptions bordered column={2} size="small">
          <Descriptions.Item label="Location">
            {event.location}
          </Descriptions.Item>
          <Descriptions.Item label="Start Time">
            {new Date(event.startTime).toLocaleString()}
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={STATUS_COLOR[event.status] ?? "default"}>
              {event.status}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Club">
            {event.club?.name ??
              (event.clubId ? (
                event.clubId
              ) : (
                <Text type="secondary">Not attached</Text>
              ))}
          </Descriptions.Item>
          <Descriptions.Item label="Team">
            {event.team?.name ??
              (event.teamId ? event.teamId : <Text type="secondary">—</Text>)}
          </Descriptions.Item>
          <Descriptions.Item label="Registration link" span={2}>
            {event.registrationUrl ? (
              <a href={event.registrationUrl} target="_blank" rel="noreferrer">
                {event.registrationUrl}
              </a>
            ) : (
              "Attach a club to generate the club registration link"
            )}
          </Descriptions.Item>
          {event.description ? (
            <Descriptions.Item label="Description" span={2}>
              {event.description}
            </Descriptions.Item>
          ) : null}
        </Descriptions>

        <Divider />

        <Space wrap>
          <Form layout="inline">
            <Form.Item label="Change Status">
              <Select
                value={event.status}
                onChange={handleStatusChange}
                style={{ width: 160 }}
                options={[
                  { value: "DRAFT", label: "DRAFT" },
                  { value: "ACTIVE", label: "ACTIVE" },
                  { value: "COMPLETED", label: "COMPLETED" },
                  { value: "CANCELLED", label: "CANCELLED" },
                ]}
              />
            </Form.Item>
          </Form>

          {event.playerCardTemplateKey ? (
            <Popconfirm
              title="Lock all scores for this event?"
              description="This cannot be undone. All scores will be read-only."
              onConfirm={handleLock}
              okText="Lock"
              cancelText="Cancel"
            >
              <Button danger icon={<LockOutlined />} loading={lockingScores}>
                Lock All Scores
              </Button>
            </Popconfirm>
          ) : (
            <Space direction="vertical" size={0}>
              <Button danger icon={<LockOutlined />} disabled>
                Lock All Scores
              </Button>
              <Text type="secondary">Upload a player card template before locking scores.</Text>
            </Space>
          )}

          {event.leaderboardUrl ? (
            <Button
              icon={<TrophyOutlined />}
              onClick={() =>
                window.open(
                  event.leaderboardUrl,
                  "_blank",
                  "noopener,noreferrer"
                )
              }
            >
              View Leaderboard
            </Button>
          ) : null}
        </Space>
      </Card>

      {/* ── Tabs: Employees + Player Cards ────────────────────────────────── */}
      <Tabs
        defaultActiveKey="employees"
        items={[
          {
            key: "employees",
            label: "Employees",
            children: (
              <Card title="Employees">
                {employeesQuery.isLoading ? (
                  <Spin />
                ) : (
                  <>
                    {employees.length > 0 && (
                      <Table
                        size="small"
                        dataSource={employees}
                        rowKey="id"
                        pagination={false}
                        style={{ marginBottom: 16 }}
                        columns={[
                          {
                            title: "Full Name",
                            render: (_: any, emp: EventEmployee) =>
                              emp.employee.fullName ||
                              emp.employee.username ||
                              emp.employee.email,
                          },
                          {
                            title: "Email",
                            dataIndex: ["employee", "email"],
                          },
                          {
                            title: "Allowed Metrics",
                            dataIndex: "allowedMetrics",
                            render: (metrics: string[]) => (
                              <Space wrap size={[4, 4]}>
                                {metrics.map((m) => {
                                  const found = ALL_METRICS.find(
                                    (x) => x.value === m
                                  );
                                  return (
                                    <Tag key={m}>{found ? found.label : m}</Tag>
                                  );
                                })}
                              </Space>
                            ),
                          },
                          {
                            title: "Can Lock Score",
                            dataIndex: "canLockScore",
                            render: (val: boolean) =>
                              val ? (
                                <Tag color="orange">Yes</Tag>
                              ) : (
                                <Tag>No</Tag>
                              ),
                          },
                          {
                            title: "Actions",
                            render: (_: any, emp: EventEmployee) => (
                              <Space>
                                <Button
                                  size="small"
                                  icon={<EditOutlined />}
                                  onClick={() => openEditAssignmentModal(emp)}
                                />
                                <Popconfirm
                                  title="Remove this employee?"
                                  onConfirm={() =>
                                    handleRemoveEmployee(emp.employee.id)
                                  }
                                  okText="Remove"
                                  cancelText="Cancel"
                                >
                                  <Button
                                    danger
                                    size="small"
                                    icon={<DeleteOutlined />}
                                    loading={
                                      removingEmployee[emp.employee.id] ?? false
                                    }
                                  />
                                </Popconfirm>
                              </Space>
                            ),
                          },
                        ]}
                        locale={{ emptyText: "No employees assigned" }}
                      />
                    )}

                    <Divider orientation="left" plain>
                      Assign Employee
                    </Divider>
                    <Form layout="inline">
                      <Row gutter={12} align="bottom" style={{ width: "100%" }}>
                        <Col xs={24} sm={9}>
                          <Form.Item label="User" style={{ marginBottom: 8 }}>
                            <UserSearchSelect
                              style={{ minWidth: 420 }}
                              userType={[
                                "ADMIN",
                                "SUPER_ADMIN",
                                "EMPLOYEE",
                                "USER",
                              ]}
                              onInviteRequested={(query) =>
                                openInviteModal(query)
                              }
                              onChange={(userId) =>
                                setAssignState((prev) => ({ ...prev, userId }))
                              }
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={9}>
                          <Form.Item
                            label="Allowed Metrics"
                            style={{ marginBottom: 8 }}
                          >
                            <Select
                              mode="multiple"
                              style={{ minWidth: 280 }}
                              placeholder="All metrics (default)"
                              value={assignState.metrics}
                              onChange={(metrics) =>
                                setAssignState((prev) => ({ ...prev, metrics }))
                              }
                              options={ALL_METRICS}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={4}>
                          <Form.Item
                            label="Can Lock Score"
                            style={{ marginBottom: 8, whiteSpace: "nowrap" }}
                          >
                            <Switch
                              checked={assignState.canLockScore}
                              onChange={(checked) =>
                                setAssignState((prev) => ({
                                  ...prev,
                                  canLockScore: checked,
                                }))
                              }
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={2}>
                          <Form.Item style={{ marginBottom: 8, marginTop: 8 }}>
                            <Button
                              type="primary"
                              icon={<PlusOutlined />}
                              onClick={handleAssignEmployee}
                              disabled={!assignState.userId}
                              loading={assigning}
                            >
                              Assign
                            </Button>
                          </Form.Item>
                        </Col>
                      </Row>
                    </Form>
                  </>
                )}
              </Card>
            ),
          },
          {
            key: "player-cards",
            label: "Player Cards",
            children: (
              <Card title="Player Cards">
                {playerCardsQuery.isLoading ? (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      padding: 32,
                    }}
                  >
                    <Spin />
                  </div>
                ) : playerCards.length === 0 ? (
                  <Empty description="No player cards yet" />
                ) : (
                  <Row gutter={[16, 16]}>
                    {playerCards.map((card) => (
                      <Col key={card.id} xs={24} sm={12} md={8} lg={6} xl={4}>
                        <Card
                          hoverable
                          size="small"
                          cover={
                            <img
                              alt={card.playerName ?? "Player card"}
                              src={card.imageUrl}
                              style={{
                                objectFit: "contain",
                                padding: 8,
                                background: "#fafafa",
                              }}
                              onClick={() =>
                                setCardModal({
                                  open: true,
                                  imageUrl: card.imageUrl,
                                  playerName: card.playerName,
                                  playerEmail: card.playerEmail,
                                })
                              }
                            />
                          }
                        >
                          <Card.Meta
                            title={
                              card.playerName ??
                              card.playerEmail ??
                              "Unknown player"
                            }
                            description={
                              <Space size={4} wrap>
                                {card.playerEmail ? (
                                  <Text
                                    type="secondary"
                                    style={{ fontSize: 12 }}
                                  >
                                    {card.playerEmail}
                                  </Text>
                                ) : null}
                                {card.isPinned ? (
                                  <Tag color="gold">Pinned</Tag>
                                ) : null}
                                {card.rank > 0 ? (
                                  <Tag color="blue">Rank {card.rank}</Tag>
                                ) : null}
                                {!card.revealed ? <Tag>Unrevealed</Tag> : null}
                              </Space>
                            }
                          />
                        </Card>
                      </Col>
                    ))}
                  </Row>
                )}
              </Card>
            ),
          },
        ]}
      />

      <Modal
        title="Edit Assignment"
        open={editAssignmentModal.open}
        confirmLoading={editAssignmentModal.saving}
        okText="Save"
        onOk={handleUpdateAssignment}
        onCancel={() => {
          if (editAssignmentModal.saving) return;
          setEditAssignmentModal({
            open: false,
            employeeUserId: "",
            metrics: [],
            canLockScore: false,
            saving: false,
          });
        }}
      >
        <Space direction="vertical" style={{ width: "100%" }} size={16}>
          <div>
            <div style={{ marginBottom: 8 }}>Allowed Metrics</div>
            <Select
              mode="multiple"
              style={{ width: "100%" }}
              placeholder="Select metrics"
              value={editAssignmentModal.metrics}
              onChange={(metrics) =>
                setEditAssignmentModal((prev) => ({ ...prev, metrics }))
              }
              options={ALL_METRICS}
            />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span>Can Lock Score</span>
            <Switch
              checked={editAssignmentModal.canLockScore}
              onChange={(checked) =>
                setEditAssignmentModal((prev) => ({
                  ...prev,
                  canLockScore: checked,
                }))
              }
            />
          </div>
        </Space>
      </Modal>

      <Modal
        title="Invite Employee"
        open={inviteModal.open}
        confirmLoading={invitingEmployee}
        okText="Invite and Assign"
        onOk={handleInviteAndAssign}
        onCancel={() => {
          if (invitingEmployee) return;
          setInviteModal({ open: false, email: "", fullName: "" });
        }}
      >
        <Space direction="vertical" style={{ width: "100%" }} size={12}>
          <Input
            placeholder="Full name"
            value={inviteModal.fullName}
            onChange={(e) =>
              setInviteModal((prev) => ({ ...prev, fullName: e.target.value }))
            }
          />
          <Input
            placeholder="Email"
            value={inviteModal.email}
            onChange={(e) =>
              setInviteModal((prev) => ({ ...prev, email: e.target.value }))
            }
          />
        </Space>
      </Modal>

      <PlayerCardModal
        open={cardModal.open}
        onClose={() => setCardModal({ open: false })}
        imageUrl={cardModal.imageUrl}
        playerName={cardModal.playerName}
        playerEmail={cardModal.playerEmail}
      />
    </div>
  );
}
